/**
 * usePushSubscription — subscribes the browser to web push using our VAPID public key.
 *
 * Returns { status, supported, permission, subscribe, unsubscribe, sendTest, deviceCount }.
 * status: "idle" | "subscribing" | "subscribed" | "denied" | "unsupported" | "error"
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "./api";

function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const safe = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(safe);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export default function usePushSubscription() {
  const supported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  const [status, setStatus] = useState(supported ? "idle" : "unsupported");
  const [permission, setPermission] = useState(
    supported ? Notification.permission : "default",
  );
  const [deviceCount, setDeviceCount] = useState(0);

  // Refresh device count when the user logs in / opens settings.
  const refreshCount = useCallback(async () => {
    try {
      const { data } = await api.get("/push/subscriptions");
      setDeviceCount(data?.count || 0);
    } catch (err) { /* unauth or offline — device count stays stale */ if (process.env.NODE_ENV === "development") console.debug("[push] deviceCount refresh:", err?.message); }
  }, []);

  useEffect(() => {
    if (!supported) return;
    refreshCount();
    // Reflect existing local subscription status.
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => { if (sub) setStatus("subscribed"); })
      .catch((err) => { if (process.env.NODE_ENV === "development") console.debug("[push] getSubscription:", err?.message); });
  }, [supported, refreshCount]);

  const subscribe = useCallback(async () => {
    if (!supported) return { ok: false, reason: "unsupported" };
    setStatus("subscribing");
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        setStatus(perm === "denied" ? "denied" : "idle");
        return { ok: false, reason: perm };
      }
      const { data } = await api.get("/push/vapid-public-key");
      if (!data?.configured || !data?.public_key) {
        setStatus("error");
        return { ok: false, reason: "vapid_missing" };
      }
      const reg = await navigator.serviceWorker.ready;
      // If an old subscription exists with a different key, drop it first.
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        try { await existing.unsubscribe(); } catch (e) { console.warn("[push] old subscription unsubscribe failed:", e?.message); }
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      // Send subscription to backend.
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      });
      setStatus("subscribed");
      await refreshCount();
      return { ok: true };
    } catch (err) {
      console.error("push subscribe error", err);
      setStatus("error");
      return { ok: false, reason: "exception", err };
    }
  }, [supported, refreshCount]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        try { await sub.unsubscribe(); } catch (e) { console.warn("[push] unsubscribe failed:", e?.message); }
        try { await api.post("/push/unsubscribe", { endpoint }); } catch (e) { console.warn("[push] server unsubscribe failed:", e?.message); }
      }
      setStatus("idle");
      await refreshCount();
    } catch (err) {
      console.error("push unsubscribe error", err);
    }
  }, [supported, refreshCount]);

  const sendTest = useCallback(async () => {
    try {
      const { data } = await api.post("/push/test");
      return data;
    } catch (err) {
      console.error("push test error", err);
      return { sent: 0 };
    }
  }, []);

  return { supported, status, permission, deviceCount, subscribe, unsubscribe, sendTest, refreshCount };
}
